import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts'
import { useFirestoreGeneral, useFirestoreArrayContainsOrderBy } from '../../firebase/useFirestore'
import useHierarchyData from '../../hooks/useHierarchyData'
import pairRespondentScores, { resolvePairKey } from '../../helpers/analysisHelpers/pairRespondentScores'
import pearsonCorrelation from '../../helpers/analysisHelpers/correlation'
import DeepAnalysisAIContext from '../../StateManagment/DeepAnalysisAIContext'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import ComparisonFrame from '../dataVisualizations/ComparisonFrame'
import periodRangeToMs from '../../helpers/compareHelpers/periodRangeToMs'
import sideSummariesForAI from '../../helpers/compareHelpers/sideSummariesForAI'
import DeepAnalysisSectionHeader from './DeepAnalysisSectionHeader'
import useChartColors from '../../hooks/useChartColors'

// Resolves an effect's first indicator and fetches its response records
// within the period (null range = all data).
const useEffectRecords = (effectId, range) => {
    const fields = useFirestoreArrayContainsOrderBy(
        'QuestionnaireFields',
        'EffectId',
        effectId || '123',
        'IndicatorPosition',
        'asc'
    )
    const indicator = Array.isArray(fields) ? fields[0] : undefined
    const isMsiMatrix = indicator
        && (indicator.Type === 'matrix-one' || indicator.Type === 'matrix-multiple')
        && indicator.Standard === 'true'

    const { startMs, endMs } = periodRangeToMs(range)
    return useHierarchyData({
        syncType: indicator ? (isMsiMatrix ? 'MSIEffect' : 'Question') : null,
        id: indicator?.ID,
        startDate: startMs,
        endDate: endMs,
    })
}

/**
 * One period's pair scatter: fetches both effects within `range`, pairs the
 * respondents and reports the correlation for Decca through `onSummary`
 * (undefined while not ready).
 *
 * @param {object} props
 * @param {object} props.effect - The anchor Effect doc
 * @param {string} props.compareId - ID of the second effect
 * @param {object|undefined} props.compareEffect - The second Effect doc (may still be loading)
 * @param {{start: string|null, end: string|null}|null} props.range - Period to fetch (null = all data)
 * @param {'A'|'B'} props.side - Which period this is
 * @param {(side: 'A'|'B', summary: object|undefined) => void} props.onSummary - Receives the Decca summary once loaded
 */
const PairPanel = ({ effect, compareId, compareEffect, range, side, onSummary }) => {
    const { palette } = useChartColors()
    // Paletpositie, geen huisstijlkleur — zie docs/patterns.md#ui-chart-palette.
    const kleurPunt = palette[1]

    const { records: recordsA, loading: loadingA, error: errorA } = useEffectRecords(effect.ID, range)
    const { records: recordsB, loading: loadingB, error: errorB } = useEffectRecords(compareId, range)

    const pairs = useMemo(() => {
        const key = resolvePairKey(recordsA, recordsB)
        return pairRespondentScores(recordsA, recordsB, { key })
    }, [recordsA, recordsB])

    const correlation = useMemo(() => pearsonCorrelation(pairs), [pairs])

    const loading = loadingA || loadingB
    const error = errorA || errorB
    const compareTitle = compareEffect?.Effect || ''

    useEffect(() => {
        onSummary(side, loading || error ? undefined : {
            vergelekenMet: compareTitle,
            r: correlation.r !== null && !correlation.insufficient
                ? Math.round(correlation.r * 100) / 100
                : null,
            gedeeldeDeelnemers: correlation.n,
        })
    }, [onSummary, side, loading, error, compareTitle, correlation])

    const maxScore = pairs.reduce((max, p) => Math.max(max, p.x, p.y), 0)

    if (loading) return <p>Gegevens worden geladen…</p>
    if (error) {
        return (
            <p className='deep-analysis-error'>
                De gegevens konden niet worden geladen: {String(error.message || error)}
            </p>
        )
    }
    if (pairs.length === 0) {
        return (
            <p className='deep-analysis-empty'>
                {range
                    ? 'Geen gedeelde deelnemers in deze periode.'
                    : 'Geen gedeelde deelnemers gevonden tussen deze twee effecten.'}
            </p>
        )
    }

    return (
        <>
            <div className='deep-analysis-scatter'>
                <ResponsiveContainer width='100%' height={320}>
                    <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis type='number' dataKey='x' name={effect.Effect} domain={[0, Math.ceil(maxScore)]} />
                        <YAxis type='number' dataKey='y' name={compareTitle} domain={[0, Math.ceil(maxScore)]} />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                        <Scatter data={pairs} fill={kleurPunt} />
                    </ScatterChart>
                </ResponsiveContainer>
            </div>
            <p className='deep-analysis-scatter-count'>
                {correlation.insufficient || correlation.r === null
                    ? `Te weinig gedeelde deelnemers voor een betrouwbare correlatie (n = ${correlation.n}).`
                    : `Correlatie r = ${correlation.r.toFixed(2)} over ${correlation.n} gedeelde deelnemers.`}
            </p>
        </>
    )
}

/**
 * Correlation scatter between the anchor effect and a second effect, shown
 * when the URL carries `?compare=<effectId>` (e.g. from a correlation-matrix
 * cell). One dot per respondent: X = score on the anchor, Y = on the other.
 *
 * Follows the page period (DeepAnalysisPeriodContext); when two periods are
 * compared it draws one scatter per period side by side.
 *
 * @param {object} props
 * @param {object} props.effect - The anchor Effect doc
 */
const EffectPairSection = ({ effect }) => {
    const location = useLocation()
    const compareId = new URLSearchParams(location.search).get('compare') || ''
    const period = useContext(DeepAnalysisPeriodContext)
    const compare = Boolean(period?.compareActive)

    const compareDocs = useFirestoreGeneral('OutputEffects', 'ID', compareId)
    const compareEffect = Array.isArray(compareDocs) ? compareDocs[0] : undefined

    // One summary per period; Decca gets them apart (sideSummariesForAI).
    const [summaries, setSummaries] = useState(/** @type {{A?: object, B?: object}} */ ({}))
    const onSummary = useCallback((side, summary) => {
        // Equality guard on content (like `publish`): a fresh but equal
        // summary object must not trigger another render.
        setSummaries((prev) => (
            JSON.stringify(prev[side]) === JSON.stringify(summary) ? prev : { ...prev, [side]: summary }
        ))
    }, [])

    // Publish the open pair comparison for Decca (deep-analysis AI section).
    // Closed, or a period still loading/errored: clear the key so a previous
    // pair's (or period's) summary never lingers.
    const aiContext = useContext(DeepAnalysisAIContext)
    const publish = aiContext?.publish
    const forAI = useMemo(() => sideSummariesForAI(summaries, period), [summaries, period])
    useEffect(() => {
        if (!publish) return
        publish('geopendeVergelijking', compareId ? forAI : null)
    }, [publish, compareId, forAI])

    if (!compareId) return null

    const panel = (range, side) => (
        <PairPanel
            effect={effect}
            compareId={compareId}
            compareEffect={compareEffect}
            range={range}
            side={side}
            onSummary={onSummary}
        />
    )

    return (
        <div className='deep-analysis-section'>
            <DeepAnalysisSectionHeader
                kicker='Correlatie'
                title={`Correlatie met ${compareEffect ? `"${compareEffect.Effect}"` : 'ander effect'}`}
                description={`Iedere stip is één deelnemer: naar rechts de score op "${effect.Effect}", omhoog de score op ${compareEffect ? `"${compareEffect.Effect}"` : 'het andere effect'}.`}
            />
            {compare
                ? <ComparisonFrame rangeA={period.rangeA} rangeB={period.rangeB} renderViz={panel} />
                : panel(period?.rangeA || null, 'A')}
        </div>
    )
}

export default EffectPairSection
